// Intersection Observer based preloading for better performance

export const createIntersectionPreloader = () => {
  const preloadedComponents = new Set();
  
  const preloadComponent = (componentName) => {
    if (preloadedComponents.has(componentName)) return;
    
    preloadedComponents.add(componentName);
    
    switch (componentName) {
      case 'skills':
        import('../components/Skills');
        break;
      case 'projects':
        import('../components/Projects');
        break;
      case 'contact':
        import('../components/Contact');
        break;
      default:
        break;
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const componentName = entry.target.dataset.component;
          if (componentName) {
            preloadComponent(componentName);
          }
        }
      });
    },
    {
      rootMargin: '200px 0px', // Start preloading 200px before component comes into view
      threshold: 0.1
    }
  );

  return {
    observe: (element, componentName) => {
      element.dataset.component = componentName;
      observer.observe(element);
    },
    disconnect: () => observer.disconnect()
  };
};

// Preload components based on scroll position
export const preloadOnScroll = () => {
  const preloader = createIntersectionPreloader();
  
  // Create invisible trigger elements for each section
  const createTrigger = (componentName, offset = 0) => {
    const trigger = document.createElement('div');
    trigger.style.position = 'absolute';
    trigger.style.top = `${offset}px`;
    trigger.style.height = '1px';
    trigger.style.width = '1px';
    trigger.style.pointerEvents = 'none';
    trigger.style.opacity = '0';
    
    const section = document.getElementById(componentName);
    if (section) {
      section.style.position = 'relative';
      section.appendChild(trigger);
      preloader.observe(trigger, componentName);
    }
  };

  // Create triggers for each section
  createTrigger('skills', -300); // Preload 300px before skills section
  createTrigger('projects', -300); // Preload 300px before projects section
  createTrigger('contact', -300); // Preload 300px before contact section

  return preloader;
};

// Immediate preloading for critical components
export const immediatePreload = () => {
  // Preload Skills component immediately as it's likely to be seen first
  import('../components/Skills');
  
  // Preload others with a small delay
  setTimeout(() => {
    import('../components/Projects');
  }, 50);
  
  setTimeout(() => {
    import('../components/Contact');
  }, 100);
};
